import { View, ActivityIndicator, Alert as NativeAlert, Platform } from "react-native"
import * as Burnt from "burnt"
import { Controller, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"

import { trpc } from "utils/trpc"
import { ar } from "i18n/ar"
import { notificationMessageInput, previewAudienceInput, type z } from "@white-labels/input"
import { Alert as WebAlert } from "@src/components/ui/confirm"
import { Text, TextInput, Button, ModalWrapper } from "./core"

const ConfirmationAlert = Platform.OS === "web" ? WebAlert : NativeAlert

type NotificationComposerProps = {
  audience: z.infer<typeof previewAudienceInput>
  onClose: () => void
}

export function NotificationComposer({ audience, onClose }: NotificationComposerProps) {
  const utils = trpc.useUtils()
  // Reuse the shared API schema so form validation can never drift from the
  // backend contract.
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm({ resolver: zodResolver(notificationMessageInput), defaultValues: { title: "", body: "" }, mode: "onChange" })
  const preview = trpc.notifications.previewAudience.useQuery(audience, { staleTime: 30_000 })

  const send = trpc.notifications.send.useMutation({
    onSuccess: (result) => {
      Burnt.toast({
        title: `${ar.notificationAccepted}: ${result.acceptedCount} · ${ar.notificationRejected}: ${result.rejectedCount} · ${ar.notificationSkipped}: ${result.skippedCount}`,
        preset: result.acceptedCount > 0 ? "done" : "error",
      })
      // Keep the notifications page history list in sync; harmless elsewhere.
      void utils.notifications.history.invalidate()
      onClose()
    },
    onError: (e) => Burnt.toast({ title: e.message, preset: "error" }),
  })

  const onSubmit = handleSubmit((data) => {
    // A second React Native Modal cannot be presented reliably from inside the
    // composer Modal. Use the platform alert on mobile and keep the custom
    // dialog only for web, where nested native modal presentation is irrelevant.
    ConfirmationAlert.alert(ar.sendNotification, ar.confirmSendMessage, [
      { text: ar.cancel, style: "cancel" },
      {
        text: ar.notificationSend,
        onPress: () => send.mutate({ ...audience, ...data }),
      },
    ])
  })

  const previewEligible = preview.data?.eligibleCount ?? 0
  const exceedsLimit = preview.data?.exceedsSendLimit
  const previewReady = preview.data && !preview.isError
  // Send is enabled only when the form is valid and there is at least one
  // eligible recipient. A failed or loading preview keeps sending disabled.
  const canSend = isValid && !!previewReady && previewEligible > 0 && !exceedsLimit && !send.isPending

  return (
    <ModalWrapper onClose={onClose}>
      <Text className="text-xl font-bold mb-2">{ar.sendNotification}</Text>

      <View className="mb-3">
        {preview.isLoading ? (
          <ActivityIndicator />
        ) : preview.isError ? (
          <Text className="text-red-600 text-xs">{ar.errorLoading}</Text>
        ) : (
          <>
            {/* Authoritative sendable-recipient count from the shared resolver.
              Intentionally no reachability badge here — its semantics differ
              from the customer-summary badge and could mislead the send decision. */}
            <Text className="text-gray-500 text-xs">
              {ar.notificationEligible}: {previewEligible}
            </Text>
            {exceedsLimit && <Text className="text-red-600 text-xs mt-1">{ar.notificationSendLimit}</Text>}
            {previewEligible === 0 && <Text className="text-gray-500 text-xs mt-1">{ar.notificationNoRecipients}</Text>}
          </>
        )}
      </View>

      <Controller
        control={control}
        name="title"
        render={({ field: { onChange, value } }) => <TextInput placeholder={ar.notificationTitle} value={value} onChangeText={onChange} className="mb-1" />}
      />

      <Controller
        control={control}
        name="body"
        render={({ field: { onChange, value } }) => (
          <TextInput placeholder={ar.notificationBody} value={value} onChangeText={onChange} multiline numberOfLines={3} className="mb-1" />
        )}
      />

      <Button onPress={onSubmit} loading={send.isPending} disabled={!canSend} className="mt-2">
        {ar.notificationSend}
      </Button>
    </ModalWrapper>
  )
}
